import { Navigation } from "@/components/shared/common/Navigation";
import { Footer } from "@/components/shared/common/Footer";
import { ThemeToggle } from "@/components/shared/ui/theme-toggle";
import { useAuth } from "@/hooks/useAuth";
import { AppSidebar } from "./AppSidebar";
import {
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
} from "@/components/shared/ui/sidebar";

interface AppLayoutProps {
  children: React.ReactNode;
  showNav?: boolean;
}

export const AppLayout = ({ children, showNav = true }: AppLayoutProps) => {
  const { profile } = useAuth();
  const isAuthenticated = !!profile;

  // Authenticated users get the sidebar shell
  if (isAuthenticated && showNav) {
    return (
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          {/* Minimal floating trigger — no bar, no border, no duplicate controls */}
          <div className="sticky top-0 z-30 flex h-10 items-center px-3 pt-safe-top pointer-events-none">
            <SidebarTrigger
              data-testid="nav-menu-mobile"
              className="pointer-events-auto -ml-0.5 opacity-60 hover:opacity-100 transition-opacity size-10"
            />
          </div>
          <main
            id="main"
            className="flex-1 w-full max-w-screen-xl mx-auto px-5 px-safe sm:px-8 lg:px-10 py-6"
          >
            {children}
          </main>
          <Footer />
        </SidebarInset>
      </SidebarProvider>
    );
  }

  // Public pages or no-nav pages keep the classic vertical layout
  return (
    <div
      className="min-h-screen bg-background flex flex-col overflow-x-hidden"
      style={{ background: "var(--gradient-subtle)" }}
    >
      {showNav ? (
        <Navigation />
      ) : (
        <header className="bg-background pt-safe-top" role="banner">
          <div className="container mx-auto px-4 sm:px-6 px-safe py-4">
            <div className="flex items-center justify-between">
              <div />
              <div className="flex items-center">
                <ThemeToggle />
              </div>
            </div>
          </div>
        </header>
      )}
      <main
        id="main"
        className="flex-1 w-full max-w-7xl mx-auto px-4 px-safe sm:px-6 lg:px-8 py-4 md:py-6"
      >
        {children}
      </main>
      <Footer />
    </div>
  );
};
